'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import {
  GraduationCap, User, Camera, Award, MapPin, Instagram, Youtube,
  Link2, Phone, MessageCircle, ChevronRight, Check, AlertCircle,
  Loader2, X, Upload, Image as ImageIcon, Clock
} from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { parseAuthToken } from '@/utils/base64'

const CERTIFICATION_AGENCIES = [
  'AIDA',
  'SSI',
  'PADI',
  'RAID',
  'CMAS',
  'Molchanovs',
  'Apnea Academy',
  'PFI',
  '기타'
]

const CERTIFICATION_LEVELS = {
  'AIDA': ['Instructor', 'Master Instructor', 'Instructor Trainer', 'Course Director'],
  'SSI': ['Freediving Instructor', 'Advanced Freediving Instructor', 'Instructor Trainer'],
  'PADI': ['Freediver Instructor', 'Master Freediver Instructor'],
  'RAID': ['Freediver Instructor', 'Advanced Freediver Instructor'],
  'Molchanovs': ['Wave 1 Instructor', 'Wave 2 Instructor', 'Wave 3 Instructor', 'Instructor Trainer'],
  'default': ['Instructor', 'Master Instructor', 'Instructor Trainer']
}

const SPECIALTIES = [
  '교육/강습',
  '수중촬영',
  '국내투어',
  '해외투어',
  '데일리트레이닝'
]

const REGIONS = [
  '서울/경기',
  '부산/경남',
  '대구/경북',
  '인천',
  '대전/충청',
  '광주/전라',
  '제주',
  '강원',
  '해외'
]

export default function InstructorRegisterPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const bg      = isDark ? '#0f172a' : '#f4f7fb'
  const card    = isDark ? '#1e2a3d' : '#ffffff'
  const border  = isDark ? '#2c3a52' : '#e9eff7'
  const ink     = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const accent  = isDark ? '#5ea6ff' : '#2b6df0'

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [existingRequest, setExistingRequest] = useState<any>(null)
  const [isInstructor, setIsInstructor] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isUploadingCert, setIsUploadingCert] = useState(false)
  const [certPreview, setCertPreview] = useState<string | null>(null)
  const [isUploadingProfile, setIsUploadingProfile] = useState(false)
  const [profilePreview, setProfilePreview] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    display_name: '',
    bio: '',
    profile_image_url: '',
    certification_agency: '',
    certification_level: '',
    certification_image_url: '',
    years_experience: '' as unknown as number,
    main_region: '',
    specialties: [] as string[],
    instagram_url: '',
    youtube_url: '',
    blog_url: '',
    kakao_id: '',
    kakao_open_chat_url: '',
    contact_phone: '',
    bank_name: '',
    bank_account: '',
    bank_holder: '',
    deposit_amount: 10000,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editMode, setEditMode] = useState(false)
  const [editRequestId, setEditRequestId] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const editId = params.get('edit')
    if (editId) setEditRequestId(editId)
    checkAuth(editId || undefined)
  }, [])

  const checkAuth = async (editId?: string) => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) { setIsLoggedIn(false); setIsLoading(false); return }

      const payload = parseAuthToken(token)
      if (!payload) { setIsLoggedIn(false); setIsLoading(false); return }

      const supabase = getSupabaseClient()

      let userQuery = supabase.from('user_profiles').select('id, name, phone')
      if (payload.user_id) userQuery = userQuery.eq('id', payload.user_id)
      else if (payload.kakao_id) userQuery = userQuery.eq('kakao_id', payload.kakao_id)
      else if (payload.naver_id) userQuery = userQuery.eq('naver_id', payload.naver_id)

      const { data: userData, error: userError } = await userQuery.single()
      if (userError || !userData) { setIsLoggedIn(false); setIsLoading(false); return }

      setIsLoggedIn(true)
      setUserId(userData.id)
      setFormData(prev => ({ ...prev, display_name: userData.name || '', contact_phone: userData.phone || '' }))

      const { data: instructorData } = await supabase
        .from('instructor_profiles').select('id').eq('user_id', userData.id).single()
      if (instructorData) { setIsInstructor(true); setIsLoading(false); return }

      const { data: requestData } = await supabase
        .from('instructor_requests').select('*').eq('user_id', userData.id).single()
      if (requestData) {
        setExistingRequest(requestData)
        if (editId && requestData.id === editId && requestData.status === 'rejected') {
          setEditMode(true)
          setFormData(prev => ({
            ...prev,
            display_name: requestData.display_name || prev.display_name,
            bio: requestData.bio || '',
            profile_image_url: requestData.profile_image_url || '',
            certification_agency: requestData.certification_agency || '',
            certification_level: requestData.certification_level || '',
            certification_image_url: requestData.certification_image_url || '',
            years_experience: requestData.years_experience ?? 0,
            main_region: requestData.main_region || '',
            specialties: requestData.specialties || [],
            instagram_url: requestData.instagram_url || '',
            youtube_url: requestData.youtube_url || '',
            blog_url: requestData.blog_url || '',
            kakao_id: requestData.kakao_id || '',
            kakao_open_chat_url: requestData.kakao_open_chat_url || '',
            contact_phone: requestData.contact_phone || prev.contact_phone,
          }))
          if (requestData.certification_image_url) setCertPreview(requestData.certification_image_url)
          if (requestData.profile_image_url) setProfilePreview(requestData.profile_image_url)
        }
      }
    } catch (error) {
      console.error('Auth check error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSpecialtyToggle = (specialty: string) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }))
  }

  const compressImage = (file: File, maxWidth: number = 1920): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new window.Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let { width, height } = img
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth }
          canvas.width = width; canvas.height = height
          const ctx = canvas.getContext('2d')
          if (!ctx) { reject(new Error('Canvas context not available')); return }
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => { if (blob) resolve(blob); else reject(new Error('Failed to compress image')) }, 'image/jpeg', 0.85)
        }
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target?.result as string
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  const uploadImageViaApi = async (blob: Blob, fileName: string): Promise<string> => {
    const fd = new FormData()
    fd.append('file', new File([blob], fileName, { type: 'image/jpeg' }))
    fd.append('fileName', fileName)
    const res = await fetch('/api/instructor/upload-image', { method: 'POST', body: fd })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || '업로드 실패')
    return data.url
  }

  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !userId) return
    if (!file.type.startsWith('image/')) { alert('이미지 파일만 업로드 가능합니다.'); return }

    setIsUploadingProfile(true)
    try {
      const compressedBlob = await compressImage(file, 800)
      setProfilePreview(URL.createObjectURL(compressedBlob))
      const fileName = `${userId}/${Date.now()}_profile.jpg`
      const url = await uploadImageViaApi(compressedBlob, fileName)
      setFormData(prev => ({ ...prev, profile_image_url: url }))
    } catch (error: any) {
      alert('이미지 업로드에 실패했습니다: ' + (error.message || '알 수 없는 오류'))
      setProfilePreview(null)
    } finally { setIsUploadingProfile(false) }
  }

  const handleRemoveProfileImage = () => { setProfilePreview(null); setFormData(prev => ({ ...prev, profile_image_url: '' })) }

  const handleCertImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !userId) return
    if (!file.type.startsWith('image/')) { alert('이미지 파일만 업로드 가능합니다.'); return }

    setIsUploadingCert(true)
    try {
      const compressedBlob = await compressImage(file)
      setCertPreview(URL.createObjectURL(compressedBlob))
      const fileName = `${userId}/${Date.now()}_cert.jpg`
      const url = await uploadImageViaApi(compressedBlob, fileName)
      setFormData(prev => ({ ...prev, certification_image_url: url }))
    } catch (error: any) {
      alert('이미지 업로드에 실패했습니다: ' + (error.message || '알 수 없는 오류'))
      setCertPreview(null)
    } finally { setIsUploadingCert(false) }
  }

  const handleRemoveCertImage = () => { setCertPreview(null); setFormData(prev => ({ ...prev, certification_image_url: '' })) }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.display_name.trim()) newErrors.display_name = '강사명을 입력해주세요'
    if (!formData.certification_agency) newErrors.certification_agency = '자격 단체를 선택해주세요'
    if (!formData.certification_level) newErrors.certification_level = '자격 레벨을 선택해주세요'
    if (!formData.main_region) newErrors.main_region = '주 활동 지역을 선택해주세요'
    if (formData.specialties.length === 0) newErrors.specialties = '전문 분야를 1개 이상 선택해주세요'
    if (!formData.kakao_id.trim() && !formData.kakao_open_chat_url.trim()) newErrors.kakao_contact = '카카오톡 ID 또는 오픈채팅 URL 중 하나는 필수입니다'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch('/api/instructor/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          ...(editMode && editRequestId ? { request_id: editRequestId } : {}),
        }),
      })

      const json = await res.json()
      if (!res.ok) throw new Error(json.error || '등록 중 오류가 발생했습니다.')
      setShowSuccessModal(true)
    } catch (error: any) {
      alert(error.message || '등록 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getLevelOptions = () => {
    if (!formData.certification_agency) return []
    return CERTIFICATION_LEVELS[formData.certification_agency as keyof typeof CERTIFICATION_LEVELS] || CERTIFICATION_LEVELS['default']
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} />
      </div>
    )
  }

  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <User className={`w-10 h-10 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          </div>
          <h1 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>로그인이 필요합니다</h1>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>강사 등록을 신청하려면 먼저 로그인해주세요.</p>
          <button onClick={() => router.push('/login')} className="px-6 py-3 text-white font-medium rounded-xl transition-all" style={{ background: accent }}>로그인하기</button>
        </div>
      </div>
    )
  }

  if (isInstructor) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>이미 강사로 등록되어 있습니다</h1>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>투어 등록 페이지에서 투어를 등록하거나<br />프로필을 수정할 수 있습니다.</p>
          <div className="flex flex-col gap-3">
            <button onClick={() => router.push('/tours/register')} className="px-6 py-3 text-white font-medium rounded-xl transition-all" style={{ background: accent }}>투어 등록하기</button>
            <button onClick={() => router.push('/instructor/edit')} className={`px-6 py-3 font-medium rounded-xl transition-all ${isDark ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>강사 프로필 수정</button>
          </div>
        </div>
      </div>
    )
  }

  if (existingRequest && !editMode) {
    const statusConfig = {
      pending: { color: 'text-yellow-500', bg: 'bg-yellow-500/20', text: '검토 대기중' },
      approved: { color: 'text-green-500', bg: 'bg-green-500/20', text: '승인됨' },
      rejected: { color: 'text-red-500', bg: 'bg-red-500/20', text: '거절됨' }
    }
    const status = statusConfig[existingRequest.status as keyof typeof statusConfig] || statusConfig.pending
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full ${status.bg} flex items-center justify-center`}>
            <GraduationCap className={`w-10 h-10 ${status.color}`} />
          </div>
          <h1 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>강사 등록 요청 {status.text}</h1>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {existingRequest.status === 'pending' && (
              <>
                강사 등록이 처리 중입니다.<br />
                잠시 후 대시보드에서 확인해주세요.
              </>
            )}
            {existingRequest.status === 'approved' && (
              <>
                강사 등록이 승인되었습니다!<br />
                이제 투어를 등록할 수 있습니다.
              </>
            )}
            {existingRequest.status === 'rejected' && (
              <>
                강사 등록이 거절되었습니다.<br />
                {existingRequest.admin_note && (
                  <span className="block mt-2 text-red-400">
                    사유: {existingRequest.admin_note}
                  </span>
                )}
              </>
            )}
          </p>
          {existingRequest.status === 'approved' && <button onClick={() => router.push('/tours/register')} className="px-6 py-3 text-white font-medium rounded-xl transition-all" style={{ background: accent }}>투어 등록하기</button>}
          {existingRequest.status === 'rejected' && (
            <>
              <button onClick={async () => {
                if (!confirm('새로 강사 등록을 신청하시겠습니까?\n기존 신청 내역은 삭제됩니다.')) return
                try {
                  const supabase = getSupabaseClient()
                  const { error } = await supabase.from('instructor_requests').delete().eq('id', existingRequest.id)
                  if (error) throw error
                  setExistingRequest(null)
                } catch (error: any) { alert('삭제 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류')) }
              }} className="px-6 py-3 text-white font-medium rounded-xl transition-all" style={{ background: accent }}>다시 신청하기</button>
              <p className={`text-xs mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>문의사항은 커뮤니티 &gt; 문의게시판을 이용해주세요.</p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`sub-sticky z-20 ${isDark ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-xl border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => router.back()} className={isDark ? 'text-gray-400' : 'text-gray-500'}>←</button>
            <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editMode ? '강사 등록 수정 (재제출)' : '강사 등록 신청'}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-40">
        <div className="p-4 rounded-xl mb-6" style={{ background: isDark ? '#1e3a5f22' : '#dbeafe', border: `1px solid ${isDark ? '#1e3a5f' : '#bfdbfe'}` }}>
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: accent }} />
            <div className="text-sm" style={{ color: isDark ? '#93c5fd' : '#1e40af' }}>
              <p className="font-medium mb-1">강사 등록 안내</p>
              <ul className="text-xs space-y-0.5 opacity-80">
                <li>• 프리다이빙 강사 자격증이 필요합니다</li>
                <li>• <strong>첫 번째 투어는 이용료 없이 무료</strong>로 등록됩니다</li>
                <li>• 등록 즉시 투어를 올릴 수 있습니다</li>
                <li>• 자격증 첨부 시 인증 배지가 부여됩니다</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 기본 정보 */}
        <section className="mb-8">
          <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}><User className="w-5 h-5" />기본 정보</h2>
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>강사명 (표시될 이름) *</label>
              <input type="text" value={formData.display_name} onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))} placeholder="예: 제주 프리다이버 민수" className={`w-full px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border ${errors.display_name ? 'border-red-500' : ''}`} />
              {errors.display_name && <p className="text-red-500 text-xs mt-1">{errors.display_name}</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>자기소개</label>
              <textarea value={formData.bio} onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))} placeholder="간단한 자기소개를 작성해주세요" rows={3} className={`w-full px-4 py-3 rounded-xl text-sm resize-none ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`} />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>프로필 사진</label>
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>강사 목록과 상세 페이지에 표시됩니다.</p>
              {profilePreview || formData.profile_image_url ? (
                <div className="relative w-32">
                  <div className={`relative w-32 h-32 rounded-full overflow-hidden border-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <img src={profilePreview || formData.profile_image_url} alt="프로필 미리보기" className="w-full h-full object-cover" />
                    <button type="button" onClick={handleRemoveProfileImage} className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                  <p className={`text-xs mt-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>✓ 이미지가 업로드되었습니다</p>
                </div>
              ) : (
                <label className={`flex flex-col items-center justify-center w-32 h-32 rounded-full border-2 border-dashed cursor-pointer transition-all ${isDark ? 'border-gray-700 hover:border-[#2b6df0] bg-gray-800/50 hover:bg-gray-800' : 'border-gray-300 hover:border-[#2b6df0] bg-gray-50 hover:bg-gray-100'} ${isUploadingProfile ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <div className="flex flex-col items-center justify-center">
                    {isUploadingProfile ? <><Loader2 className={`w-8 h-8 mb-1 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-500'}`} /><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>업로드 중...</p></> : <><Camera className={`w-8 h-8 mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} /><p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>사진 추가</p></>}
                  </div>
                  <input type="file" accept="image/*" onChange={handleProfileImageUpload} disabled={isUploadingProfile} className="hidden" />
                </label>
              )}
            </div>
          </div>
        </section>

        {/* 자격 정보 */}
        <section className="mb-8">
          <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}><Award className="w-5 h-5" />자격 정보</h2>
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>자격 단체 *</label>
              <select value={formData.certification_agency} onChange={(e) => setFormData(prev => ({ ...prev, certification_agency: e.target.value, certification_level: '' }))} className={`w-full px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border ${errors.certification_agency ? 'border-red-500' : ''}`}>
                <option value="">선택해주세요</option>
                {CERTIFICATION_AGENCIES.map(agency => <option key={agency} value={agency}>{agency}</option>)}
              </select>
              {errors.certification_agency && <p className="text-red-500 text-xs mt-1">{errors.certification_agency}</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>자격 레벨 *</label>
              <select value={formData.certification_level} onChange={(e) => setFormData(prev => ({ ...prev, certification_level: e.target.value }))} disabled={!formData.certification_agency} className={`w-full px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border ${errors.certification_level ? 'border-red-500' : ''} ${!formData.certification_agency ? 'opacity-50' : ''}`}>
                <option value="">선택해주세요</option>
                {getLevelOptions().map(level => <option key={level} value={level}>{level}</option>)}
              </select>
              {errors.certification_level && <p className="text-red-500 text-xs mt-1">{errors.certification_level}</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>자격증 사진 (선택)</label>
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>자격증 이미지를 첨부하시면 승인이 더 빠르게 처리됩니다.</p>
              {certPreview || formData.certification_image_url ? (
                <div className="relative">
                  <div className={`relative rounded-xl overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <img src={certPreview || formData.certification_image_url} alt="자격증 미리보기" className="w-full h-48 object-contain bg-gray-100 dark:bg-gray-800" />
                    <button type="button" onClick={handleRemoveCertImage} className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                  <p className={`text-xs mt-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>✓ 이미지가 업로드되었습니다</p>
                </div>
              ) : (
                <label className={`flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all ${isDark ? 'border-gray-700 hover:border-[#2b6df0] bg-gray-800/50 hover:bg-gray-800' : 'border-gray-300 hover:border-[#2b6df0] bg-gray-50 hover:bg-gray-100'} ${isUploadingCert ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <div className="flex flex-col items-center justify-center py-4">
                    {isUploadingCert ? <><Loader2 className={`w-10 h-10 mb-2 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-500'}`} /><p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>업로드 중...</p></> : <><Upload className={`w-10 h-10 mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} /><p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>자격증 사진 업로드</p><p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>JPG, PNG (자동 압축)</p></>}
                  </div>
                  <input type="file" accept="image/*" onChange={handleCertImageUpload} disabled={isUploadingCert} className="hidden" />
                </label>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>경력 (년)</label>
              <input type="number" value={formData.years_experience === ('' as any) ? '' : formData.years_experience} onChange={(e) => setFormData(prev => ({ ...prev, years_experience: e.target.value === '' ? '' as any : parseInt(e.target.value) || 0 }))} min={0} max={50} placeholder="0" className={`w-full px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`} />
            </div>
          </div>
        </section>

        {/* 활동 정보 */}
        <section className="mb-8">
          <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}><MapPin className="w-5 h-5" />활동 정보</h2>
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>주 활동 지역 *</label>
              <select value={formData.main_region} onChange={(e) => setFormData(prev => ({ ...prev, main_region: e.target.value }))} className={`w-full px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border ${errors.main_region ? 'border-red-500' : ''}`}>
                <option value="">선택해주세요</option>
                {REGIONS.map(region => <option key={region} value={region}>{region}</option>)}
              </select>
              {errors.main_region && <p className="text-red-500 text-xs mt-1">{errors.main_region}</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>전문 분야 * (복수 선택 가능)</label>
              <div className="flex flex-wrap gap-2">
                {SPECIALTIES.map(specialty => (
                  <button key={specialty} type="button" onClick={() => handleSpecialtyToggle(specialty)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${formData.specialties.includes(specialty) ? 'text-white' : isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    style={formData.specialties.includes(specialty) ? { background: accent } : {}}>{specialty}</button>
                ))}
              </div>
              {errors.specialties && <p className="text-red-500 text-xs mt-1">{errors.specialties}</p>}
            </div>
          </div>
        </section>

        {/* 카카오톡 연락처 */}
        <section className="mb-8">
          <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}><MessageCircle className="w-5 h-5" />카카오톡 연락처 *</h2>
          <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>투어 참가 신청 시 다이버들이 연락할 수 있는 카카오톡 정보입니다.<br /><strong>카카오톡 ID 또는 오픈채팅 URL 중 하나는 필수</strong>로 입력해주세요.</p>
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>카카오톡 ID</label>
              <div className="flex items-center gap-3">
                <MessageCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <input type="text" value={formData.kakao_id} onChange={(e) => setFormData(prev => ({ ...prev, kakao_id: e.target.value }))} placeholder="예: diving_master" className={`flex-1 px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border ${errors.kakao_contact ? 'border-red-500' : ''}`} />
              </div>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>카카오톡 설정 &gt; 프로필 &gt; 카카오톡 ID에서 확인 가능</p>
            </div>
            <div className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><span className="text-xs">또는</span></div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>오픈채팅 URL</label>
              <div className="flex items-center gap-3">
                <Link2 className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <input type="text" value={formData.kakao_open_chat_url} onChange={(e) => setFormData(prev => ({ ...prev, kakao_open_chat_url: e.target.value }))} placeholder="예: https://open.kakao.com/o/xxxxx" className={`flex-1 px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border ${errors.kakao_contact ? 'border-red-500' : ''}`} />
              </div>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>카카오톡 &gt; 오픈채팅 &gt; 링크 복사</p>
            </div>
            {errors.kakao_contact && <p className="text-red-500 text-xs">{errors.kakao_contact}</p>}
          </div>
        </section>

        {/* SNS/연락처 */}
        <section className="mb-8">
          <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}><Link2 className="w-5 h-5" />SNS / 기타 연락처 (선택)</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3"><Instagram className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} /><input type="text" value={formData.instagram_url} onChange={(e) => setFormData(prev => ({ ...prev, instagram_url: e.target.value }))} placeholder="Instagram URL" className={`flex-1 px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`} /></div>
            <div className="flex items-center gap-3"><Youtube className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} /><input type="text" value={formData.youtube_url} onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))} placeholder="YouTube URL" className={`flex-1 px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`} /></div>
            <div className="flex items-center gap-3"><Link2 className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} /><input type="text" value={formData.blog_url} onChange={(e) => setFormData(prev => ({ ...prev, blog_url: e.target.value }))} placeholder="블로그 URL" className={`flex-1 px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`} /></div>
            <div className="flex items-center gap-3"><Phone className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} /><input type="text" value={formData.contact_phone} onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))} placeholder="연락처 (선택)" className={`flex-1 px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`} /></div>
          </div>
        </section>

        {/* 예약금 계좌 */}
        <section className="mb-8">
          <h2 className={`text-base font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>예약금 입금 계좌</h2>
          <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>다이버가 투어 신청 시 이 계좌로 예약금을 보냅니다. 나중에 언제든 수정 가능합니다.</p>
          <div className={`rounded-2xl overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
              <div className="flex items-center px-4 py-3 gap-3">
                <span className={`text-xs w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>은행</span>
                <input type="text" value={formData.bank_name} onChange={e => setFormData(prev => ({ ...prev, bank_name: e.target.value }))} placeholder="카카오뱅크" className={`flex-1 text-sm bg-transparent outline-none ${isDark ? 'text-white placeholder-gray-600' : 'text-gray-900 placeholder-gray-300'}`} />
              </div>
              <div className="flex items-center px-4 py-3 gap-3">
                <span className={`text-xs w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>계좌번호</span>
                <input type="text" value={formData.bank_account} onChange={e => setFormData(prev => ({ ...prev, bank_account: e.target.value }))} placeholder="3333-00-0000000" className={`flex-1 text-sm bg-transparent outline-none ${isDark ? 'text-white placeholder-gray-600' : 'text-gray-900 placeholder-gray-300'}`} />
              </div>
              <div className="flex items-center px-4 py-3 gap-3">
                <span className={`text-xs w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>예금주</span>
                <input type="text" value={formData.bank_holder} onChange={e => setFormData(prev => ({ ...prev, bank_holder: e.target.value }))} placeholder="홍길동" className={`flex-1 text-sm bg-transparent outline-none ${isDark ? 'text-white placeholder-gray-600' : 'text-gray-900 placeholder-gray-300'}`} />
              </div>
              <div className="flex items-center px-4 py-3 gap-3">
                <span className={`text-xs w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>예약금</span>
                <input type="number" value={formData.deposit_amount} onChange={e => setFormData(prev => ({ ...prev, deposit_amount: parseInt(e.target.value) || 0 }))} step={1000} min={0} className={`w-28 text-sm bg-transparent outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>원</span>
              </div>
            </div>
          </div>
        </section>

      </div>

      <div className={`fixed above-nav left-0 right-0 p-4 ${isDark ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-xl border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-2xl mx-auto">
          <button onClick={handleSubmit} disabled={isSubmitting} className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{ background: isSubmitting ? '#6b7280' : accent }}>
            {isSubmitting ? <><Loader2 className="w-5 h-5 animate-spin" />신청 중...</> : <>강사 등록 신청하기<ChevronRight className="w-5 h-5" /></>}
          </button>
        </div>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center"><Clock className="w-8 h-8 text-amber-500" /></div>
              <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>신청이 접수되었습니다</h3>
              <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                강사 등록 신청이 완료되었습니다.<br />
                관리자 검토 후 승인되면 알림을 보내드립니다.
              </p>
              <p className={`text-xs mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>보통 1~2 영업일 내 처리됩니다.</p>
              <button onClick={() => router.push('/')} className="w-full py-3 rounded-xl font-medium text-white transition-all" style={{ background: accent }}>홈으로 돌아가기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
